import {
    CommunityId,
    CustomListItemId,
    ReferenceBusinessId,
    ReferenceContactId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import { ActionBarComponent } from "components/shared/action-bar-component";
import { Select } from "components/ui/select";
import React from "react";
import { useFeature } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { tierColorForToken } from "./tier-colors";

type Tier = inferProcedureOutput<AppRouter["referralTier"]["tiers"]>[number];
type SourceState = inferProcedureOutput<AppRouter["referralTier"]["sourceState"]>;

type TierSubjectType = "business" | "contact" | "community";

interface ReferenceTierProps {
    sourceType: TierSubjectType;
    sourceId: string;
}

const useReferenceTier = (sourceType: TierSubjectType, sourceId: string) => {
    const featureEnabled = useFeature("referralTiers");
    const { settings } = useTenantSettings();
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();

    const enabled = featureEnabled && !!settings.referralTiersEnabled;

    const tiersQuery = useQuery({
        ...trpcClient.referralTier.tiers.queryOptions(),
        enabled,
    });
    const stateOptions = trpcClient.referralTier.sourceState.queryOptions({
        sourceType,
        sourceId,
    });
    const stateQuery = useQuery({ ...stateOptions, enabled });

    const onSuccess = () => {
        queryClient.invalidateQueries({ queryKey: stateOptions.queryKey });
    };

    const setBusiness = useMutation({
        ...trpcClient.referralTier.setBusinessTier.mutationOptions(),
        onSuccess,
    });
    const clearBusiness = useMutation({
        ...trpcClient.referralTier.clearBusinessTier.mutationOptions(),
        onSuccess,
    });
    const setContact = useMutation({
        ...trpcClient.referralTier.setContactTier.mutationOptions(),
        onSuccess,
    });
    const clearContact = useMutation({
        ...trpcClient.referralTier.clearContactTier.mutationOptions(),
        onSuccess,
    });
    const setCommunity = useMutation({
        ...trpcClient.referralTier.setCommunityTier.mutationOptions(),
        onSuccess,
    });
    const clearCommunity = useMutation({
        ...trpcClient.referralTier.clearCommunityTier.mutationOptions(),
        onSuccess,
    });

    const tiers: Tier[] = tiersQuery.data ?? [];
    const stateData: SourceState | undefined = stateQuery.data;
    const ownId = stateData?.ownTierListItemId ?? null;
    const effectiveId = stateData?.effectiveTierListItemId ?? null;
    const effectiveTier = tiers.find(t => t.id === effectiveId) ?? null;
    const inherited = sourceType === "contact" && !ownId && !!effectiveId;

    const setTier = (value: string) => {
        const tierListItemId = value as CustomListItemId;
        if (sourceType === "business") {
            const businessId = sourceId as ReferenceBusinessId;
            if (value) {
                setBusiness.mutate({ businessId, tierListItemId });
            } else {
                clearBusiness.mutate({ businessId });
            }
        } else if (sourceType === "contact") {
            const contactId = sourceId as ReferenceContactId;
            if (value) {
                setContact.mutate({ contactId, tierListItemId });
            } else {
                clearContact.mutate({ contactId });
            }
        } else {
            const communityId = sourceId as CommunityId;
            if (value) {
                setCommunity.mutate({ communityId, tierListItemId });
            } else {
                clearCommunity.mutate({ communityId });
            }
        }
    };

    return { enabled, tiers, ownId, effectiveTier, inherited, setTier };
};

export const ReferenceTierField: React.FC<ReferenceTierProps> = props => {
    const { enabled, effectiveTier } = useReferenceTier(props.sourceType, props.sourceId);

    if (!enabled || !effectiveTier) {
        return null;
    }

    const color = tierColorForToken(effectiveTier.color);
    return (
        <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Referral tier</dt>
            <dd className="mt-1 text-sm text-gray-900">
                <span
                    className={`inline-block flex-shrink-0 rounded-full ${color.bg} ${color.text} px-3 py-0.5 text-sm font-medium`}>
                    {effectiveTier.name}
                </span>
            </dd>
        </div>
    );
};

export const ReferenceTierActionBar: React.FC<ReferenceTierProps> = props => {
    const { enabled, tiers, ownId, effectiveTier, inherited, setTier } = useReferenceTier(
        props.sourceType,
        props.sourceId,
    );

    if (!enabled) {
        return null;
    }

    const emptyLabel =
        inherited && effectiveTier
            ? `Inherited from org (${effectiveTier.name})`
            : "No tier";

    return (
        <ActionBarComponent label="Referral tier" value={effectiveTier?.name ?? "None"}>
            <div className="p-4">
                <Select
                    label="Referral Tier"
                    value={ownId ?? ""}
                    onChange={e => setTier(e.target.value)}>
                    <option value="">{emptyLabel}</option>
                    {tiers
                        .filter(t => !t.isArchived)
                        .map(t => (
                            <option key={t.id} value={t.id}>
                                {t.name}
                            </option>
                        ))}
                </Select>
            </div>
        </ActionBarComponent>
    );
};
